<template>
  <div class="order-area">
    <div class="order-area__wrapper">
      <div
        v-if="showQuantityRange"
        class="order-area__quantity">
        <product-promo-block
          v-if="promoBannerText"
          class="order-area__promo-block">
          {{ promoBannerText }}
        </product-promo-block>

        <div class="order-area__range-wrapper">
          <div class="order-area__block-title">
            {{ quantityRangeTitle }}: {{ runsCount }}
          </div>

          <ui-range
            :value="rangeValues.runs"
            :zones="rangeZones.runs"
            :show-scale="false"
            class="order-area__range"
            @input="setRangeValues('runs', $event)"
            @change="emitRangeStatistics('runs')" />
        </div>
      </div>

      <div class="order-area__cost">
        <div
          v-if="!!discountPercent"
          class="order-area__discount">
          <div
            :key="discountPercent"
            class="discount__value">
            {{ discountPercent }}% <br> OFF
          </div>
        </div>

        <div
          v-if="showOldPrice"
          :key="currencyValue(oldPrice)"
          class="cost__old">
          {{ currencyValue(oldPrice) }}
        </div>

        <div class="cost__new">
          <upper-cents-price
            :key="price + 'cents'"
            :sum="price || 0"
            class="cost__new-value" />
        </div>
      </div>

      <div class="order-area__cashback">
        <cashback-badge
          v-if="showCashbackBadge"
          :price="price"
          :type="isCurrencyFinanceReport ? 'product-gold' : 'product'"
          class="dropdown-widget__cashback order-area__cashback-badge">
          cashback
          <info-tooltip text="Will be added to your Skycoach balance" />
        </cashback-badge>

        <cashback-switcher
          v-if="product.is_enabled_cashback_writeoff"
          :price="price"
          :old-price="oldPrice"
          :type="isCurrencyFinanceReport ? 'gold' : 'default'"
          :is-payment-method-opened="isPaymentMethodOpened"
          class="order-area__cashback-switcher"
          @cashback-applied="handleCashbackApplied" />
      </div>

      <div
        v-if="canBuy && havePromocodeAvailable && !userHasCashback"
        class="order-area__promocode">
        <div
          v-if="!isPromocodeInputVisible && !appliedPromocode && !promocodeError"
          class="promocode__title"
          @click="showPromocodeInput">
          I have a promo code
        </div>

        <div
          v-else-if="promocodeError && !appliedPromocode"
          class="promocode__error">
          {{ promocodeError }}
        </div>

        <div
          v-else-if="appliedPromocode && !promocodeError"
          class="promocode__error">
          Applied:&nbsp;
          <span class="promocode__name">
            {{ appliedPromocode.code.toUpperCase() }}
          </span>

          <span
            class="shape-cross promocode__decline"
            @click="declinePromocode" />
        </div>

        <div
          v-if="isPromocodeInputVisible"
          class="promocode__input-wrapper">
          <input
            ref="promocodeInput"
            v-model="promocode"
            :disabled="appliedPromocode"
            :placeholder="appliedPromocode ? 'PROMOCODE' : 'Enter promo code'"
            autocomplete="off"
            name="code"
            type="text"
            class="promocode__input transition--color-bg"
            @keyup.enter="applyPromocode">

          <div
            :class="{ 'promocode__submit--error': promocodeError }"
            class="promocode__submit"
            @click="applyPromocode">
            <div
              v-if="promocodeError"
              class="promocode__submit-icon" />

            <div
              v-else-if="appliedPromocode"
              class="shape-check" />

            <i
              v-else
              class="arrow right" />
          </div>
        </div>
      </div>

      <ui-input
        v-if="!user && canBuy"
        v-model.trim="email"
        :error="emailError || boosterError"
        name="email"
        inputmode="email"
        autocomplete="off"
        class="order-area__email"
        placeholder="Enter your email *"
        @keyup.native.enter="openPaymentMethodSelector"
        @input="emailError = '', boosterError = ''" />

      <div
        v-if="userIsBooster || boosterError"
        class="button button--disabled order-area__buy-now">
        Pro can't buy
      </div>

      <div
        v-else-if="!isAvailable"
        class="button button--disabled order-area__buy-now">
        Sold out
      </div>

      <div
        v-else-if="product.meta && product.meta.unavailableText"
        class="button button--disabled order-area__buy-now">
        {{ product.meta.unavailableText }}
      </div>

      <div
        v-else
        class="button button-main order-area__buy-now"
        @click.stop="openPaymentMethodSelector">
        <img
          data-src="/images/icons/shield.svg"
          class="lazyload order-area__buy-now--img"
          width="14"
          height="14"
          alt="">
        {{ loading ? 'Please wait...' : 'Buy Now' }}
      </div>

      <div
        v-if="isStripeButtonVisible"
        class="button order-area__buy-now"
        :class="{ 'google-pay-btn': availableStripePaymentMethods.googlePay }"
        @click.stop="buyWithStripe">
        <template v-if="loading">
          Please wait...
        </template>

        <template v-else>
          <img
            v-if="availableStripePaymentMethods.googlePay"
            alt=""
            src="/images/payment_systems/google-pay.svg"
            class="payment-button__img">
          <span v-else>
            Buy Now
          </span>
        </template>
      </div>

      <div class="order-area__payment-systems">
        <img
          v-for="item in paymentMethods"
          :key="item.id"
          class="payment-item"
          height="20"
          width="55"
          alt=""
          :src="`/images/payment_systems/white/${item.pic}`">
      </div>

      <div class="order-area__bottom-separator">
        <div class="separator-text">
          or
        </div>
      </div>

      <div class="order-area__chat">
        <div class="chat__info">
          <div class="any-questions">
            Any questions?
          </div>

          <div class="before-order">
            Chat before ordering
          </div>
        </div>

        <div
          class="chat__button button button-additional"
          @click="openIntercom">
          <img
            data-src="/images/icons/chat2.svg"
            width="24"
            height="24"
            alt=""
            class="lazyload chat__button-img">
          <div class="chat__button-text">
            Chat
          </div>
        </div>
      </div>
    </div>

    <sezzle-widget
      v-show="shouldShowSezzle"
      :price="sezzlePrice" />

    <div class="order-area__bottom">
      <div class="we-price-match">
        <div class="we-price-match__icon">
          {{ currencySymbol() }}
        </div>

        <div class="we-price-match__text">
          Found cheaper? <br> We price match
        </div>
      </div>

      <span
        class="whats-next"
        @click="openWhatsNextPopup">
        What’s next?
      </span>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

import PaymentSystemModel from 'Models/PaymentSystemModel';
import UserModel from 'Models/UserModel';
import UpperCentsPrice from 'Components/ui/Price/UpperCentsPrice.vue';
import UiInput from 'Components/ui/Input/UiInput.vue';

import { applyPromocode } from 'Root/helpers/api';
import googleTagAnalytics from 'Root/drivers/googleTagAnalytics';
import IntercomApiDriver from 'Root/drivers/IntercomApiDriver';
import { POPUP_PROMOCODE_APPLIED } from 'Models/PopupModel';
import { regExpEmail } from 'Root/helpers/regexps';
import HttpErrorModel from 'Models/HttpErrorModel';
import CashbackBadge from 'Components/CashbackBadge.vue';
import CashbackSwitcher from 'Components/CashbackSwitcher.vue';
import UiRange from 'Components/UiRange.vue';
import ProductPromoBlock from 'Root/pages/game/components/ProductPromoBlock.vue';
import { DISCOUNT_BY_QUANTITY } from 'Models/ProductDiscountModel';
import PromocodeModel from 'Models/PromocodeModel';
import SezzleWidget from 'Components/SezzleWidget.vue';
import tiktokTagAnalytics from 'Root/drivers/tiktokTagAnalytics';
import InfoTooltip from 'Components/ui/Tooltip/InfoTooltip.vue';

export default {
  name: 'OrderArea',

  components: {
    SezzleWidget,
    ProductPromoBlock,
    UiRange,
    UiInput,
    UpperCentsPrice,
    CashbackBadge,
    CashbackSwitcher,
    InfoTooltip,
  },

  props: {
    oldPrice: {
      type: Number,
      default: null,
    },

    price: {
      type: Number,
      default: null,
    },

    orderError: {
      type: String,
      default: null,
    },

    product: {
      type: Object,
      required: true,
    },

    loading: {
      type: Boolean,
      default: false,
    },

    isAvailable: {
      type: Boolean,
      default: false,
    },

    availableStripePaymentMethods: {
      type: Object,
      default: () => ({}),
    },

    error: {
      type: String,
      default: null,
    },

    isPaymentMethodOpened: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isPromocodeInputVisible: false,
      promocode: '',
      appliedPromocode: null,
      promocodeError: null,
      emailError: '',
      email: '',
      boosterError: '',
      usedCashback: null,

      rangeZones: {
        runs: [{
          from: 1,
          to: this.product.meta?.quantityMaxRange || 15,
          size: 100,
          step: 1,
        }],
      },
      rangeValues: {
        runs: [1],
      },
    };
  },

  computed: {
    ...mapState(['userDevice', 'activeRegion', 'user', 'countryCode']),
    ...mapGetters(['currencyValue', 'currencySymbol', 'userCashback']),

    sezzlePrice() {
      return this.currencyValue(this.price + this.price * (PaymentSystemModel.METHODS.SEZZLE.mulModifier / 100));
    },

    shouldShowSezzle() {
      const sezzle = PaymentSystemModel.METHODS.SEZZLE;

      return (sezzle.onlyForUsersWithId ? sezzle.onlyForUsersWithId.includes(this.user?.id) : true)
        && (sezzle.enabled && sezzle.regions.includes(this.activeRegion.id))
        && (!sezzle.regionsCode || sezzle.regionsCode.includes(this.countryCode))
        && this.price >= sezzle.minValue
        && this.price <= sezzle.maxValue;
    },

    paymentMethods() {
      return PaymentSystemModel.METHODS_LIST
        .filter((method) => method.id !== PaymentSystemModel.PAYMENTWALL.id)
        .filter((method, index, list) => list
          .findIndex((item) => (item.title === method.title)) === index);
    },

    discountPercent() {
      const { price, oldPrice } = this;
      const result = Math.max(Math.round(((oldPrice - price) / oldPrice) * 100), 0);
      return Number.isNaN(result) ? 0 : Math.round(result);
    },

    userIsBooster() {
      return [UserModel.ROLES.PRO.id, UserModel.ROLES.PRO_CANDIDATE.id].includes(+this.user?.role);
    },

    isStripeButtonVisible() {
      const stripeAvailable = this.availableStripePaymentMethods.googlePay;
      const userCanUseStripe = PaymentSystemModel.METHODS.GOOGLE_PAY.onlyForUsersWithId
        ? PaymentSystemModel.METHODS.GOOGLE_PAY.onlyForUsersWithId.includes(this.user?.id) : true;
      return stripeAvailable && userCanUseStripe;
    },

    canBuy() {
      return this.isAvailable && !(this.product.meta && this.product.meta.unavailableText);
    },

    havePromocodeAvailable() {
      return this.product?.promocode_regions?.find((region) => region.id === this.activeRegion.id);
    },

    showOldPrice() {
      return this.price < this.oldPrice;
    },

    userHasCashback() {
      return this.userCashback && this.userCashback.value_usa > 0;
    },

    showCashbackBadge() {
      return this.product?.is_enabled_cashback;
    },

    showQuantityRange() {
      return !this.product.meta?.hideQuantity;
    },

    quantityRangeTitle() {
      return this.product.meta?.quantityCustomTitle || 'Quantity';
    },

    currentDiscountByQuantity() {
      const discounts = this.product.discounts?.[DISCOUNT_BY_QUANTITY];

      const discount = Object.values(discounts ?? {})
        .filter((item) => item.quantity <= this.runsCount)
        .sort((a, b) => a.quantity - b.quantity);

      return discount[discount.length - 1];
    },

    priceAfterPromocode() {
      if (!this.appliedPromocode) {
        return this.price;
      }

      if (this.appliedPromocode.type === PromocodeModel.TYPE.PERCENT.id) {
        return this.price * (1 - this.appliedPromocode.value / 100);
      }

      return Math.max(this.price - this.appliedPromocode.value, 0);
    },

    promoBannerText() {
      const discountByQuantity = this.product.discounts?.[DISCOUNT_BY_QUANTITY] || {};
      const closestDiscount = Object.values(discountByQuantity)
        .find((discount) => discount.quantity > this.runsCount
          && discount.value !== discountByQuantity[this.runsCount]?.value);

      const maxCountForDiscount = Object.values(discountByQuantity)
        .sort((a, b) => b.quantity - a.quantity)[0]?.quantity;

      if (closestDiscount) {
        const data = {
          count: closestDiscount.quantity - this.runsCount,
          discount: closestDiscount.type === PromocodeModel.TYPE.PERCENT.id
            ? `${closestDiscount.value}%` : this.currencyValue(closestDiscount.value),
        };

        return `Add ${data.count} more for a ${data.discount} discount`;
      }

      if (this.runsCount >= maxCountForDiscount && discountByQuantity[maxCountForDiscount]) {
        return `${discountByQuantity[maxCountForDiscount].value}% discount applied!`;
      }

      return '';
    },

    runsCount() {
      return this.rangeValues.runs[0];
    },

    isCurrencyFinanceReport() {
      return this.product?.is_currency_offer_finance_report;
    },
  },

  watch: {
    promocode(val, oldVal) {
      if (val !== oldVal) {
        this.promocode = this.promocode.toUpperCase();
      }

      this.promocodeError = null;
    },

    error(newValue) {
      this.boosterError = newValue;
    },

    orderError() {
      this.promocode = '';

      this.showPromocodeError(this.orderError);
    },

    activeRegion() {
      this.promocode = '';
      this.appliedPromocode = null;
      this.promocodeError = '';
      this.$emit('promocode-applied', this.appliedPromocode);
    },

    product(val, oldVal) {
      if (val !== oldVal && this.promocode) {
        this.applyPromocode(true);
      }
    },

    'product.id': {
      handler() {
        this.setRangeValues('runs', [1]);
      },
    },

    currentDiscountByQuantity: {
      handler() {
        this.$emit('discount-by-quantity', this.currentDiscountByQuantity);
      },
      immediate: true,
    },

    runsCount() {
      this.$emit('runs-count', this.runsCount);
    },
  },

  mounted() {
    const promocode = this.$cookie.get(POPUP_PROMOCODE_APPLIED.cookieName);

    if (promocode) {
      this.promocode = promocode;
      this.applyPromocode(true);
    }
  },

  methods: {
    showPromocodeInput() {
      this.isPromocodeInputVisible = true;
      requestAnimationFrame(() => {
        this.$refs.promocodeInput.focus();
      });
    },

    validateEmail() {
      if (!this.email.match(regExpEmail)) {
        this.emailError = 'Please, enter a valid email';
        googleTagAnalytics.fireError('buy_now_1', 'wrong_email');
        return false;
      }
      return true;
    },

    openPaymentMethodSelector() {
      googleTagAnalytics.fireBuyClick('buy_now_1', this.product.title);
      tiktokTagAnalytics.fireAddToCart(this.user, this.price, this.activeRegion.currency.title);

      if (this.loading || (this.user ? false : !this.validateEmail())) {
        return;
      }

      if (this.promocodeError) {
        this.promocodeError = null;
      }

      this.applyPromocode()
        .then(() => this.$emit('open-payment-method-selector', this.email));
    },

    openWhatsNextPopup() {
      this.$emit('open-whats-next');
    },

    applyPromocode(isAutoApplied = false) {
      return new Promise((resolve, reject) => {
        if ((this.appliedPromocode && this.user?.email)
                || !this.promocode
                || !this.havePromocodeAvailable) {
          return resolve();
        }

        const params = {
          promocode: this.promocode,
          regionId: this.activeRegion.id,
          productId: this.product.id,
          userEmail: this.email || this.user?.email || null,
        };

        return applyPromocode(params)
          .then(({ data }) => {
            if (!data.promocode) {
              // отменяем промокод если он был подставлен автоматически
              if (isAutoApplied) {
                this.promocode = '';
              }

              this.showPromocodeError('Promotional code not found');

              if (!this.isPromocodeInputVisible) {
                this.isPromocodeInputVisible = true;
              }

              reject();
            } else if (!data.is_available) {
              // отменяем промокод если он был подставлен автоматически
              if (isAutoApplied) {
                this.promocode = '';
              }

              this.showPromocodeError(!data.promocode
                ? 'Promotional code not found'
                : (data.message || 'Unexpected error while applying promotional code'));

              if (!this.isPromocodeInputVisible) {
                this.isPromocodeInputVisible = true;
              }

              reject();
            } else {
              this.appliedPromocode = data.promocode;
              this.promocodeError = null;

              if (this.usedCashback) {
                this.handleCashbackApplied({
                  ...this.usedCashback,
                  isApplied: this.usedCashback.isApplied,
                });
              }

              this.$emit('promocode-applied', this.appliedPromocode);

              resolve();
            }
          })
          .catch((error) => {
            if (error.response.status === HttpErrorModel.NOT_FOUND) {
              this.promocodeError = error.response.data.message;
            }
            reject();
          });
      });
    },

    declinePromocode() {
      this.appliedPromocode = null;
      this.$emit('promocode-applied', this.appliedPromocode);
      this.promocode = '';
      requestAnimationFrame(() => {
        this.$refs.promocodeInput.focus();
      });
    },

    openIntercom() {
      googleTagAnalytics.emitItemOpenChatEvent();
      IntercomApiDriver.show();
    },

    buyWithStripe() {
      if (this.loading || (this.user ? false : !this.validateEmail())) {
        return;
      }

      this.$emit('buy-with-stripe', this.email);
    },

    handleCashbackApplied(cashback) {
      this.usedCashback = {
        ...cashback,
        isApplied: cashback.isApplied,
        basePrice: this.priceAfterPromocode,
      };
      this.$emit('cashback-applied', this.usedCashback);
    },

    showPromocodeError(error) {
      this.appliedPromocode = null;

      this.$nextTick(() => {
        this.promocodeError = error;
      });
    },

    setRangeValues(field, value) {
      this.rangeValues[field] = value;
    },

    emitRangeStatistics(field) {
      googleTagAnalytics.emitChooseItemOption(field);
    },
  },
};
</script>

<style lang="scss" scoped>
  // MOBILE FIRST
  .order-area__wrapper {
    position: relative;
    background-color: $color--basic-menu;
    padding: 18px 24px 28px;
    border-radius: 10px;
    margin-bottom: 20px;

    // По другому не работает, по этому вложил сюда
    .cost__new-value {
      --cent-margin: -5px 0 0 2px;
    }
  }

  .order-area__discount {
    position: absolute;
    top: 0;
    right: 0;
    background: url('/images/bg/star-discount.svg') no-repeat center;
    background-size: 100%;
    width: 64px;
    height: 64px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .discount__value {
    font-weight: 500;
    font-size: 15px;
    text-align: center;
  }

  .order-area__cost {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: baseline;
    margin-bottom: 18px;
  }

  .order-area__cashback {
    margin-bottom: 16px;
    font-size: 13px;
    font-weight: 500;
  }

  .order-area__cashback-badge {
    display: block;
    margin-bottom: 16px;
  }

  .order-area__cashback-switcher {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .cost__old {
    grid-area: old;
    color: $color--basic-white;
    font-weight: 500;
    font-size: 15px;
    text-decoration-line: line-through;
    display: flex;
    justify-content: flex-end;
  }

  .cost__new {
    grid-area: new;
    font-weight: 500;
    font-size: 35px;
    line-height: 41px;
  }

  .order-area__promocode {
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-bottom: 8px;
  }

  .promocode__title {
    font-weight: 500;
    font-size: $text-13px;
    border-bottom: 1px dashed white;
    cursor: pointer;
    padding-bottom: 2px;
    width: max-content;
    margin-bottom: 15px;
  }

  .promocode__error {
    display: flex;
    align-items: center;
    font-weight: 500;
    font-size: $text-13px;
    color: $color--basic-light-gray;
    margin-bottom: 16px;
  }

  .promocode__name {
    color: $color--basic-white;
    text-transform: uppercase;
  }

  .promocode__decline {
    --thickness: 1px;

    width: 12px;
    height: 12px;
    margin: 0 0 0 6px;
  }

  .promocode__input-wrapper {
    display: flex;
    position: relative;
    width: 100%;
  }

  .promocode__input {
    padding: 0 43px 0 13px;
    height: 40px;
    font-family: inherit;
    font-weight: 500;
    font-size: $text-13px;
    color: $color--basic-light-gray;
    background-color: $color--basic-dark-gray;
    width: 100%;
    border-radius: 5px;
    outline: none;
    border: none;

    &::placeholder {
      font-weight: 500 !important;
      font-size: $text-13px;
      color: $color--basic-light-gray;
      opacity: 1 !important;
    }

    &:focus-within {
      background-color: $color--basic-superdark-gray;
    }
  }

  .promocode__submit {
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    width: 32px;
    height: 32px;
    position: absolute;
    right: 5px;
    top: 50%;
    transform: translateY(-50%);

    .arrow {
      border: solid $color--basic-light-gray;
      border-width: 0 3px 3px 0;
      display: inline-block;
      padding: 3px;
    }

    .right {
      transform: rotate(-45deg);
      margin-right: 2px;
    }
  }

  .promocode__submit-icon {
    width: 17px;
    height: 16px;
    background-color: $color--basic-light-gray;
    -webkit-mask: url(/images/icons/alert.svg) no-repeat center;
    mask: url(/images/icons/alert.svg) no-repeat center;
  }

  .shape-check {
    --shape-color: #{$color--basic-light-gray};
    --thickness: 3px;

    width: 16px;
    height: 16px;
    transition: all 0.1s ease;
  }

  .order-area__email {
    --height: 40px !important;
    --font-size: 13px !important;
    --color: #{$color--basic-light-gray} !important;
    --background-color: #{$color--basic-dark-gray} !important;
    --padding: 8px 13px !important;

    margin-bottom: 8px;
  }

  .order-area__buy-now {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 40px;
    margin-bottom: 16px;
    text-transform: none;
    font-size: $text-13px;
  }

  .order-area__buy-now--img {
    margin-right: 9px;
    height: auto;
  }

  .order-area__payment-systems {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    margin-bottom: 10px;
  }

  .google-pay-btn {
    background-color: black;
    padding: 11px;

    &:hover {
      background-color: #3C4043;
    }
  }

  .payment-item {
    filter: $filter--white-to-dark-gray;
    min-height: 20px;
    width: auto;
    margin: 0 4px 4px;
  }

  .order-area__bottom-separator {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 11px;
  }

  .separator-text {
    position: relative;
    width: 100%;
    text-align: center;
    color: $color--basic-dark-gray;
    font-weight: 500;
    font-size: $text-13px;

    &:after, &:before {
      content: '';
      width: 45%;
      border-bottom: solid 1px $color--basic-dark-gray;
      position: absolute;
      top: 50%;
    }

    &:before {
      left: 0;
    }

    &:after {
      right: 0;
    }
  }

  .order-area__chat {
    display: flex;
    justify-content: space-between;
    text-transform: none;
  }

  .any-questions {
    color: $color--basic-dark-gray;
    font-weight: 400;
    font-size: $text-11px;
    margin-bottom: 4px;
  }

  .before-order {
    font-weight: 500;
    font-size: $text-13px;
  }

  .chat__button {
    display: flex;
    align-items: center;
    padding: 5px 14px 7px 9px;
    border-radius: 55px;
    font-size: $text-13px;
    margin-left: 10px;
    text-transform: none;
  }

  .chat__button-img {
    margin-right: 5px;
  }

  .order-area__bottom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 21px;
  }

  .we-price-match {
    display: flex;
    align-items: center;
    color: $color--basic-dark-gray;
  }

  .we-price-match__icon {
    font-weight: 500;
    font-size: 35px;
    line-height: 41px;
    margin-right: 9px;
  }

  .we-price-match__text, .whats-next {
    font-weight: 500;
    font-size: $text-13px;
  }

  .whats-next {
    cursor: pointer;
    border-bottom: 1px dashed $color--basic-white;
    padding-bottom: 3px;
  }

  .whats-next__item:not(:first-child) {
    margin-top: 6px;
  }

  .order-area__range {
    width: 100%;
    font-size: 16px; // для em'ов
    padding: 0 16px;
  }

  .order-area__promo-block {
    --gradient-color: #{$color--basic-blue};
    --bg-color: #{$color--basic-menu};
    --icon-background: #{$color--basic-blue};
    --icon-url: url(/images/icons/discount.svg);

    width: 100%;
    margin-bottom: 16px;
  }

  .order-area__range-wrapper {
    margin-bottom: 12px;

    .order-area__range {
      --line-bg-color: #{$color--basic-superdark-gray};
    }
  }

  .order-area__block-title {
    margin-bottom: 8px;
    font-size: 13px;
    font-weight: 500;
  }

  // PC
  @media screen and #{$screen-more-or-1024} {
    .order-area__wrapper {
      padding: 17px 20px 20px;

      .cost__new-value {
        --cent-margin: -7px 0 0 2px;
      }
    }

    .cost__new {
      font-size: 35px;
      line-height: 41px;
      margin-right: 5px;
    }

    .order-area__buy-now {
      padding: 15px 0;
      height: 48px;
      margin-bottom: 9px;
      font-size: $text-15px;
    }

    .order-area__buy-now--img {
      width: 20px;
    }

    .order-area__payment-systems {
      margin-bottom: 14px;
    }

    .order-area__bottom-separator {
      margin-bottom: 10px;
    }

    .order-area__range-wrapper {
      margin-bottom: 18px;
    }

    .order-area__block-title {
      font-size: 15px;
    }
  }

  @media screen and (min-width: 1300px) {
    .order-area__payment-systems {
      display: flex;
      justify-content: center;
      flex-wrap: wrap;
      margin-bottom: 4px;
      width: 230px;
    }

    .payment-item {
      filter: $filter--white-to-dark-gray;
      flex-basis: 51px;
      min-height: 18px;
      height: 18px;
      width: auto;
      margin: 0 8px 8px 0;

      &:nth-child(n + 4) {
        margin-right: 0;
      }
    }
  }
</style>
